import React, { useState } from "react";
import { Form, Input, Typography, Spin, Button, Checkbox } from "antd";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import AuthServ from "../service/AuthServ";
import Notify from "../util/Notify";
import AuthVisualHero from "./AuthVisualHero";

const { Text } = Typography;

const OTPLoginComponent = ({ title = "Login", registerPath = "/customer-register" }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    // Check if value is email or mobile
    const detectIdentifierType = (value) => {
        if (!value) return null;
        const trimmed = value.trim();

        // Check if it's an email (contains @)
        if (trimmed.includes('@')) {
            return "email";
        }

        // Check if it's a mobile number (only digits)
        if (/^\d+$/.test(trimmed)) {
            return "mobile";
        }

        return "email"; // Default to email for partial inputs
    };

    const handleLogin = async (values) => {
        const identifierType = detectIdentifierType(values.identifier);

        // Validate based on type
        if (identifierType === "email") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(values.identifier)) {
                Notify.error(null, "Please enter a valid email address");
                return;
            }
        } else if (identifierType === "mobile") {
            if (!/^\d{10}$/.test(values.identifier)) {
                Notify.error(null, "Please enter a valid 10-digit mobile number");
                return;
            }
        }

        setLoading(true);
        try {
            // Send as email field - backend will handle both
            const response = await AuthServ.login(values.identifier, values.password);

            const token = response?.data?.token;
            const user = response?.data?.user;

            if (!token) throw new Error("Authentication token not received.");

            // Decode token for role
            const decoded = jwtDecode(token);
            const role = (decoded.role || user?.role || "").toString().toLowerCase();

            // 🛑 CRITICAL: Check account status BEFORE saving session
            if (user?.isActive === false || user?.isBlocked === true) {
                Notify.error("Access Denied", "Your account is currently inactive or blocked. Please contact support.");
                setLoading(false);
                return;
            }

            // Save to localStorage
            localStorage.setItem("token", token);
            localStorage.setItem("customerToken", token);
            localStorage.setItem("role", role);
            localStorage.setItem("user", JSON.stringify(user));

            // Role-based navigation - IMMEDIATE without blocking
            const queryParams = new URLSearchParams(location.search);
            const redirectUrl = queryParams.get("redirect");

            if (role === "superadmin" || role === "admin") {
                Notify.success("Welcome Admin", "Login successful!");
                navigate("/admin/dashboard");
            } else if (role === "seller") {
                Notify.success("Welcome Seller", "Login successful!");
                navigate("/seller/dashboard");
            } else if (role === "pendingseller") {
                navigate("/pending-seller");
            } else {
                Notify.success("Login Successful", "Welcome back!");
                navigate(redirectUrl || "/");
            }

            // Sync auth change & background data fetch asynchronously without blocking UI
            setTimeout(() => {
                window.dispatchEvent(new Event("auth-change"));
                
                // Background loading grouped in Promise.allSettled for optimal network waterfall
                Promise.allSettled([
                    AuthServ.single(true), // Preload profile
                    import("../service/CartServ").then(m => m.default.getCart(0,0)),
                    import("../service/WishlistServ").then(m => m.default.getWishlist()),
                    import("../service/ChatServ").then(m => m.default.getUnreadCount()),
                    import("../service/NotificationServ").then(m => m.default.getUnreadCount(role))
                ]).catch(console.error);
            }, 50);

        } catch (error) {
            console.error("Login Error:", error);
            const message = error.message || "Invalid credentials. Please try again.";
            Notify.error(null, message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="nz-auth-split-container">
            {/* 🎨 Left Side - Visual Showcase Panel */}
            <AuthVisualHero activeStep={1} />

            {/* 🚪 Right Side - Elegant, ultra-clean form panel */}
            <div className="nz-auth-form-side">
                <div className="nz-split-form-wrapper">
                    <h2>Hello Again!</h2>
                    <p className="nz-split-subtitle">
                        Welcome back! Enter your login details below to manage your Nearzon account.
                    </p>

                    <Spin spinning={loading}>
                        <Form layout="vertical" onFinish={handleLogin} requiredMark={false} size="large">
                            <Form.Item
                                label="Email or Mobile"
                                name="identifier"
                                className="nz-premium-form-item"
                                rules={[
                                    { required: true, message: "Please enter your email or mobile number!" }
                                ]}
                            >
                                <Input
                                    placeholder="Email"
                                    suffix={<span>@</span>}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Password"
                                name="password"
                                className="nz-premium-form-item"
                                rules={[
                                    { required: true, message: "Please enter your password!" }
                                ]}
                            >
                                <Input.Password
                                    placeholder="Password"
                                />
                            </Form.Item>

                            <div className="nz-form-meta-row nz-form-meta-row--right">
                                <Link to="/forgot-password" className="nz-recovery-link">
                                    Recovery Password
                                </Link>
                            </div>

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    className="nz-split-submit-btn"
                                >
                                    Login
                                </Button>
                            </Form.Item>
                        </Form>
                    </Spin>


                    <div className="nz-split-footer">
                        Don't have an account yet?{" "}
                        <Link to={registerPath} className="nz-split-footer-link">
                            Sign Up
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OTPLoginComponent;
